import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const Algae = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Algae Guide" 
        description="Identifying, preventing, and treating all types of pool algae"
      >
        <section className="space-y-8">
          {/* San Diego Context */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-foreground mb-2">San Diego Note</p>
                <p className="text-muted-foreground">
                  Year-round warm temperatures mean algae risk never goes away. Even in "winter," 
                  pools need consistent chlorine levels and circulation. Don't let your guard down 
                  just because it's not peak summer.
                </p>
              </div>
            </div>
          </div>

          {/* Algae Types */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Algae Types</h2>
            
            {/* Green Algae */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <h3 className="text-lg font-semibold text-foreground">Green Algae</h3>
                <span className="text-xs bg-muted px-2 py-1 rounded">Most Common</span>
              </div>
              <p className="text-muted-foreground mb-4">
                Free-floating algae that turns water green and makes surfaces slippery. Often appears 
                after rain, low chlorine, or extended periods without service.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Signs</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Green tint to water (light to dark)</li>
                    <li>• Slippery surfaces</li>
                    <li>• Cloudy appearance</li>
                    <li>• Green film on walls</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-2">Treatment</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Brush all surfaces thoroughly</li>
                    <li>• Shock to 10-15 ppm chlorine</li>
                    <li>• Run pump 24 hours</li>
                    <li>• Clean filter after 24 hours</li>
                    <li>• Add algaecide after chlorine drops</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Yellow/Mustard Algae */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                <h3 className="text-lg font-semibold text-foreground">Yellow/Mustard Algae</h3>
                <span className="text-xs bg-amber-500/20 text-amber-600 px-2 py-1 rounded">Resistant</span>
              </div>
              <p className="text-muted-foreground mb-4">
                Slow-growing, chlorine-resistant algae that clings to shady walls. Looks like 
                pollen or sand and can be brushed off — but comes right back.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Signs</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Yellow-brown patches on walls</li>
                    <li>• Prefers shady areas</li>
                    <li>• Brushes off easily, returns quickly</li>
                    <li>• May look like dirt or pollen</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-2">Treatment</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Brush all surfaces vigorously</li>
                    <li>• Triple shock (30+ ppm chlorine)</li>
                    <li>• Use copper-based algaecide</li>
                    <li>• Clean everything that touched the water</li>
                    <li>• May require multiple treatments</li>
                  </ul>
                </div>
              </div>
              <div className="bg-amber-500/10 rounded-lg p-3 mt-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Important:</strong> Mustard algae can survive on pool toys, 
                  floats, and cleaning equipment. Recommend client sanitize everything that's been in the pool.
                </p>
              </div>
            </div>

            {/* Black Algae */}
            <div className="bg-card border rounded-lg p-6 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-gray-800"></div>
                <h3 className="text-lg font-semibold text-foreground">Black Algae</h3>
                <span className="text-xs bg-destructive/20 text-destructive px-2 py-1 rounded">Hardest to Kill</span>
              </div>
              <p className="text-muted-foreground mb-4">
                The most stubborn algae. Forms deep roots in porous surfaces (plaster, grout) and has a 
                waxy protective coating that repels chlorine. Appears as dark spots that don't brush off easily.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Signs</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Dark black/blue-green spots</li>
                    <li>• Raised, crusty texture</li>
                    <li>• Doesn't brush off easily</li>
                    <li>• Often in tile grout or plaster cracks</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-2">Treatment</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Scrub each spot with stainless brush</li>
                    <li>• Rub chlorine tablet directly on spots</li>
                    <li>• Use penetrating algaecide (silver/copper)</li>
                    <li>• Triple shock</li>
                    <li>• May require professional treatment</li>
                  </ul>
                </div>
              </div>
              <div className="bg-destructive/10 rounded-lg p-3 mt-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-destructive">Warning:</strong> Black algae roots survive standard shocking. 
                  You must break the protective head and kill the roots. Incomplete treatment = it returns.
                </p>
              </div>
            </div>

            {/* Pink Slime */}
            <div className="bg-card border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-4 h-4 rounded-full bg-pink-400"></div>
                <h3 className="text-lg font-semibold text-foreground">Pink Slime</h3>
                <span className="text-xs bg-muted px-2 py-1 rounded">Actually Bacteria</span>
              </div>
              <p className="text-muted-foreground mb-4">
                Not actually algae — it's a bacteria (Serratia marcescens). Forms pink/reddish slime 
                in low-flow areas like skimmer baskets, light niches, and ladder steps.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Signs</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Pink/reddish slimy film</li>
                    <li>• Often around fittings and fixtures</li>
                    <li>• Common in skimmers and ladders</li>
                    <li>• May appear in white or light areas</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-2">Treatment</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Brush and scrub affected areas</li>
                    <li>• Shock to 20+ ppm chlorine</li>
                    <li>• Clean filter thoroughly</li>
                    <li>• Use algaecide formulated for bacteria</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Prevention */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Prevention</h2>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Prevention is always easier than treatment. These practices keep algae from getting 
                a foothold:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Maintain chlorine</strong> — Never let free chlorine drop below 1 ppm</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Brush weekly</strong> — Disrupts biofilm before algae can anchor</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Run pump adequately</strong> — 8-12 hours minimum, more in summer</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Weekly algaecide</strong> — Maintenance dose prevents outbreaks</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Target dead spots</strong> — Corners, behind ladders, shady areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Clean filter regularly</strong> — Dirty filter = poor circulation</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Early Detection */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Early Detection</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Catching algae early is much easier than treating a full bloom. Watch for these signs:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Slightly slippery feel on walls or steps</li>
                <li>• Small green patches in corners or shady spots</li>
                <li>• Water looking slightly "off" or hazy</li>
                <li>• Chlorine demand suddenly increasing</li>
                <li>• Faint green tint when looking at water edgewise</li>
              </ul>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> If you spot early signs, aggressive 
                  brushing + elevated chlorine (10 ppm) + extra run time can stop an outbreak before it 
                  blooms. This saves the client a full shock treatment.
                </p>
              </div>
            </div>
          </div>

          {/* When to Escalate */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">When to Escalate</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Some situations require additional support or client communication:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Black algae that isn't responding to treatment</li>
                <li>• Recurring algae despite proper maintenance</li>
                <li>• Pool is solid green (may need Green-to-Clean procedure)</li>
                <li>• Client wants to understand what happened</li>
                <li>• Algae requiring multiple shock treatments</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                Document the condition with photos and notes. Notify the office so we can 
                communicate with the client appropriately.
              </p>
            </div>
          </div>

          {/* Related */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Related Topics</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/wiki/fundamentals/chemistry" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <span className="font-medium">Water Chemistry</span>
                <p className="text-sm text-muted-foreground mt-1">Chlorine levels and pH balance</p>
              </Link>
              <Link to="/wiki/conditions/green-to-clean" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <span className="font-medium">Green-to-Clean</span>
                <p className="text-sm text-muted-foreground mt-1">Full pool recovery procedure</p>
              </Link>
            </div>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Algae;